import React, { useState, useEffect } from "react";
import DashboardModal from "@/components/dashboard-modal";
import { NewPageFormState } from "./types";
import { NewPageModalStepper } from "./NewPageModalStepper";
import { useStepNavigation } from "./hooks/useStepNavigation";
import NewPageFooter from "./NewPageFooter";
import { useToast } from "@/hooks/errors/use-toast";
import { PageType } from "../new-page-smart-detection";
import { 
  PageCreationProvider, 
  usePageCreationResearch 
} from "@/hooks/universal-creation-research";
import PageGenerationModal from "./PageGenerationModal";
import { PageGenerationRequest } from "@/types/page-generation-types";
import { OpenAIThreadsService } from "@/hooks/ai-agents/services/openai/threads.service";
import { useAIOperationWrapper } from "@/hooks/ai-usage/useAIOperationWrapper";

interface NewPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  workbookId: string; // Empty string for private pages, project ID for project pages
  onPageCreated?: (pageId: string) => void;
  // Optional initial form values
  initialTitle?: string;
  initialRequest?: string;
  initialType?: PageType;
}

// Inner component that uses the research context
const NewPageModalInner: React.FC<NewPageModalProps> = ({
  isOpen,
  onClose,
  workbookId,
  onPageCreated,
  initialTitle,
  initialRequest,
  initialType,
}) => {
  const { toast } = useToast();
  const { executeAIOperation } = useAIOperationWrapper();
  
  // Page creation research context
  const { 
    canProceedWithCreation, 
    selectedResearch, 
    summaryData, 
    resetResearchState 
  } = usePageCreationResearch();

  const [formState, setFormState] = useState<NewPageFormState>({
    title: initialTitle || "",
    initial_request: initialRequest || "",
    type: initialType || undefined,
    writing_style: "", // default writing style
  });

  // Update form state when initial props change
  useEffect(() => {
    if (initialTitle !== undefined || initialRequest !== undefined || initialType !== undefined) {
      setFormState(prevState => ({
        ...prevState,
        title: initialTitle || "",
        initial_request: initialRequest || "",
        type: initialType || prevState.type,
      }));
    }
  }, [initialTitle, initialRequest, initialType]);

  // Also update form state when modal opens with initial values
  useEffect(() => {
    if (isOpen && (initialTitle || initialRequest || initialType)) {
      setFormState(prevState => ({
        ...prevState,
        title: initialTitle || prevState.title,
        initial_request: initialRequest || prevState.initial_request,
        type: initialType || prevState.type,
      }));
    }
  }, [isOpen, initialTitle, initialRequest, initialType]);

  const [showValidation, setShowValidation] = useState(false);
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [generationRequest, setGenerationRequest] = useState<PageGenerationRequest | null>(null);

  // Single source of truth for step navigation
  const stepNavigation = useStepNavigation({
    formState,
    canProceedWithCreation,
  });

  const resetAndClose = () => {
    setFormState({
      title: initialTitle || "",
      initial_request: initialRequest || "",
      type: initialType || undefined,
      writing_style: "",
    });
    setShowValidation(false);
    resetResearchState(); // Clear research state when closing
    stepNavigation.resetToFirstStep(); // Reset to step 1
    setIsGenerationModalOpen(false);
    setGenerationRequest(null);
    onClose();
  };

  const handleSubmit = async () => {
    setShowValidation(true);
    
    if (!formState.title || formState.title.length < 3 || !formState.type) {
      toast({
        title: "Incomplete Form",
        description: "Please fill out all required fields (Title and Page Type).",
        variant: "destructive",
      });
      return;
    }

    // Check if research summaries are complete
    if (!canProceedWithCreation) {
      toast({
        title: "Research Processing",
        description: "Please wait for research summaries to complete before creating the page.",
        variant: "destructive"
      });
      return;
    }

    try {
      // 🆕 UNIVERSAL FLOW: Create dedicated OpenAI thread for this page
      console.log('[NewPageModal] Creating dedicated OpenAI thread for AI generation...');
      const threadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      console.log('[NewPageModal] Thread created:', threadId);

      // Prepare clarifying questions for page creation
      const clarifyingQuestions = formState.clarifyingQuestions?.questions
        ?.map((question, index) => ({ 
          question, 
          answer: formState.clarifyingQuestions!.answers[index] || ''
        }))
        .filter(qa => qa.answer.length > 0) || [];

      // Prepare generation request with dedicated thread
      const request: PageGenerationRequest = {
        workbook_id: workbookId || null, // Project page or private page
        title: formState.title,
        initial_request: formState.initial_request,
        type: formState.type,
        writing_style: formState.writing_style,
        goalThreadId: threadId, // ✅ UNIVERSAL: Dedicated thread for this specific page
        clarifyingQuestions: clarifyingQuestions
      };

      console.log('[NewPageModal] Starting page generation with dedicated thread:', {
        pageResearch: selectedResearch.length,
        clarifyingQuestions: clarifyingQuestions.length,
        threadId: threadId
      });

      // Set request data and open generation modal
      setGenerationRequest(request);
      setIsGenerationModalOpen(true);
    } catch (error) {
      console.error('[NewPageModal] Failed to create thread for page generation:', error);
      toast({
        title: "Thread Creation Failed",
        description: "Failed to create conversation thread. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleGenerationSuccess = (pageId: string) => {
    toast({
      title: "Page Created!",
              description: "Your new document has been created successfully.",
    });
    
    // Notify parent about page creation for auto-navigation
    onPageCreated?.(pageId);
    
    resetAndClose();
  };

  const handleGenerationClose = () => {
    setIsGenerationModalOpen(false);
    setGenerationRequest(null);
  };

  // Calculate if user has unsaved changes
  const hasUnsavedChanges = !!(
    formState.title || 
    formState.initial_request || 
    selectedResearch.length > 0 ||
    formState.clarifyingQuestions?.answers?.some(a => a.length > 0)
  );

  // Get dynamic title based on current step
  const getStepTitle = () => {
    const isPrivate = !workbookId;
    switch (stepNavigation.currentStep) {
      case 1:
        return isPrivate ? "✨ Generate New Private Document" : "✨ Generate New Document";
      case 2:
        return "🎨 Style & Research";
      case 3:
        return "🤔 Optional Questions";
      default:
        return isPrivate ? "✨ Generate New Private Document" : "✨ Generate New Document";
    }
  };

  return (
    <>
      <DashboardModal
        isOpen={isOpen && !isGenerationModalOpen}
        onClose={resetAndClose}
        title={getStepTitle()}
        size="lg"
        accentColor="purple"
        hasUnsavedChanges={hasUnsavedChanges}
      >
        <NewPageModalStepper
          value={formState}
          onChange={setFormState}
          workbookId={workbookId} // Pass workbookId (empty string for private pages)
          showValidation={showValidation}
          stepNavigation={stepNavigation}
          goalThreadId="universal-pages" // Placeholder - not used in universal flow
        />
        
        {/* Inline Footer with Step Navigation */}
        <div className="pt-6">
          <NewPageFooter
            // Step navigation props - single source of truth
            currentStep={stepNavigation.currentStep}
            totalSteps={stepNavigation.totalSteps}
            canGoNext={stepNavigation.canGoNext}
            canGoPrevious={stepNavigation.canGoPrevious}
            canSubmit={stepNavigation.canSubmit}
            onNext={stepNavigation.goToNext}
            onPrevious={stepNavigation.goToPrevious}
            
            // Legacy props for backward compatibility
            onCancel={resetAndClose}
            onSubmit={handleSubmit}
            isSubmitting={false} // No longer needed since we use the progress modal
            isValid={stepNavigation.canSubmit}
            titleCharacterCount={formState.title.length}
            purposeCharacterCount={formState.initial_request.length}
            hasType={!!formState.type}
          />
        </div>
      </DashboardModal>

      {/* Page Generation Progress Modal */}
      <PageGenerationModal
        isOpen={isGenerationModalOpen}
        onClose={handleGenerationClose}
        requestData={generationRequest}
        selectedResearch={selectedResearch}
        summaryData={summaryData}
        onSuccess={handleGenerationSuccess}
        workbookId={workbookId}
      />
    </>
  );
};

// Main component that provides context
const NewPageModal: React.FC<NewPageModalProps> = (props) => {
  return (
    <PageCreationProvider>
      <NewPageModalInner {...props} />
    </PageCreationProvider>
  );
};

export default NewPageModal;
